import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { useRawIngredients } from 'hooks/useRawIngredients';

import { CompleteList } from 'Apps/Main/Scenes/Ingredients/CompleteList';
import { GlossaryHero } from 'Apps/Main/Scenes/Ingredients/GlossaryHero';
import { NeverFoundIngredientsList } from 'Apps/Main/Scenes/Ingredients/NeverFoundIngredientsList';

import { Image } from 'Components/Image';
import { MetaData } from 'Components/MetaData';
import { TextImage } from 'Components/TextImage';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';
import { HowItWorks } from 'Blocks/HowItWorks';

import ErrorContainer from 'Containers/ErrorContainer';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';
import LoadingScene from 'Scenes/LoadingScene';

import glossary from 'assets/content/glossary';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const dataFrom = 'ingredients-index';

const CleanBeautyDescription = styled(Typography)`
  padding-bottom: ${legacyTheme.spacing.s48};
  border-bottom: 1px solid ${theme.colors.neutral[700]};

  ${legacyTheme.breakpoints.up('sm')} {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const CleanBeautyList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-items: center;
  width: 100%;
  margin-top: ${legacyTheme.spacing.s24};

  ${legacyTheme.breakpoints.up('md')} {
    grid-template-columns: 'repeat(6, auto)';
    justify-items: start;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  margin: ${legacyTheme.spacing.s16} 0;

  & img {
    margin-bottom: ${legacyTheme.spacing.s12};
  }
`;

const Glossary = () => {
  const { isMobile } = useResponsiveVariant();
  const { fetchRawIngredients, error, ingredients, isLoading } = useRawIngredients();

  // TODO: Get rid of this retry strategy, overkill for a simple informative page
  if (error) {
    return (
      <ErrorContainer
        error={error}
        retry={fetchRawIngredients}
        sentryMessage="Unhandled API error on ingredient glossary"
      />
    );
  }

  if (isLoading) {
    /* @ts-expect-error - LoadingScene has no TS types */
    return <LoadingScene />;
  }

  return (
    <>
      <MetaData
        description="Discover the natural ingredients thoughtfully curated from all around the world to create custom hair care that meets clean beauty standards. Cruelty-free."
        title="Ingredient Glossary: Best Ingredients for Hair and Skin Care | Prose"
      />

      <BaseScene footer FooterProps={{ hideCTA: true }} header>
        <GlossaryHero content={glossary.hero} dataFrom={dataFrom} />

        {/* @ts-expect-error - TextImage has no TS types */}
        <TextImage
          contentAlignMob="left"
          imageAlt={glossary.innovativeApproach.imageAlt}
          imageDesktop={glossary.innovativeApproach.image}
          imageMobile={glossary.innovativeApproach.imageMob}
          imageMode="contain-desktop"
          wideScreenOrder="21"
        >
          <Typography color="noir" markupName="h2" paragraph variant="h1r">
            {glossary.innovativeApproach.title}
          </Typography>
          <div>
            <Typography paragraph variant="p1">
              {glossary.innovativeApproach.content}
            </Typography>
          </div>
        </TextImage>

        {/* @ts-expect-error - BlockLayout has no TS types */}
        <BlockLayout
          align={isMobile ? 'left' : 'center'}
          backgroundColor="lilas"
          contentWidth="wide"
        >
          <Typography paragraph variant="h1r">
            {glossary.cleanBeautyModule.header}
          </Typography>

          <CleanBeautyDescription variant="p1">
            {glossary.cleanBeautyModule.description}
          </CleanBeautyDescription>
          <CleanBeautyList>
            {glossary.cleanBeautyModule.icons.map((icon) => (
              <Wrapper key={icon.alt}>
                <Image alt={`${icon.alt} - vector`} height="38" src={icon.src} width="38" />

                <Typography color="grey" variant="mono3">
                  {icon.legend}
                </Typography>
              </Wrapper>
            ))}
          </CleanBeautyList>
        </BlockLayout>

        <CompleteList
          content={glossary.completeList}
          dataClickButton="jump-to-letter"
          dataClickLink="view-ingredient-details"
          dataFrom={dataFrom}
          // @ts-expect-error -(@pierre)  TODO: fix TS error by getting rid of the useRawIngredients hook
          ingredients={ingredients}
        />

        <NeverFoundIngredientsList content={glossary.neverFoundIngredientsList} />

        <HowItWorks cta dataFrom={dataFrom} />
      </BaseScene>
    </>
  );
};

export default Glossary;
